import { useState, useEffect, useRef } from 'react'
import Header from './components/Header/Header'
import Hero from './components/Hero/Hero'
import Features from './components/Features/Features'
import Download from './components/Download/Download'
import Footer from './components/Footer/Footer'
import NightOverlay from './components/NightOverlay/NightOverlay'
import AboutModal from './components/AboutModal/AboutModal'
import AuthModal from './components/AuthModal/AuthModal'
import AccountModal from './components/AccountModal/AccountModal'
import AdminPanel from './components/AdminPanel/AdminPanel'
import ServerStatus from './components/ServerStatus/ServerStatus'
import DownloadNotification from './components/DownloadNotification/DownloadNotification'
import Announcements from './components/Announcements/Announcements'
import API from './services/apiService'
import './styles/App.css'

function App() {
  const [isAboutOpen, setIsAboutOpen] = useState(false)
  const [isAuthOpen, setIsAuthOpen] = useState(false)
  const [isAccountOpen, setIsAccountOpen] = useState(false)
  const [isAdminOpen, setIsAdminOpen] = useState(false)
  const [user, setUser] = useState(null)
  const [sessionMessage, setSessionMessage] = useState(null)
  const [authModalMode, setAuthModalMode] = useState('login')
  const [resetToken, setResetToken] = useState(null)
  const [unverifiedEmail, setUnverifiedEmail] = useState(null)
  const serverStatusRef = useRef(null)
  const [pendingModalAction, setPendingModalAction] = useState(null)
  const [accountInitialTab, setAccountInitialTab] = useState('info')

  // Register session invalidation callback and initialize API
  useEffect(() => {
    // Initialize API (starts token refresh if user is logged in)
    API.init()
    
    API.setSessionInvalidationCallback((message) => {
      setUser(null)
      setSessionMessage(message)
      // Close all modals
      setIsAccountOpen(false)
      setIsAdminOpen(false)
      // Auto-hide message after 5 seconds
      setTimeout(() => setSessionMessage(null), 5000)
    })
  }, [])

  // Check for saved user on mount using secure storage
  useEffect(() => {
    try {
      const savedUser = API.getCurrentUser()
      if (savedUser) {
        setUser(savedUser)
      }
    } catch (error) {
      console.error('Error loading user session:', error)
      // Clear potentially corrupted data
      localStorage.removeItem('unity-auth-token')
      localStorage.removeItem('unity-auth-user')
    }
  }, [])

  // Check for password reset token or register mode in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('token')
    const modal = urlParams.get('modal')
    const isResetPage = window.location.pathname.includes('reset-password')
    
    if (token && isResetPage) {
      setResetToken(token)
      setAuthModalMode('reset')
      setIsAuthOpen(true)
      
      // Clean up URL without refreshing the page
      window.history.replaceState({}, document.title, window.location.pathname.replace('/reset-password', '/'))
    } else if (modal === 'register') {
      // Store the action to be executed after user state is determined
      setPendingModalAction('register')
      
      // Clean up URL without refreshing the page
      window.history.replaceState({}, document.title, window.location.pathname)
    } else if (modal === 'buy-tabs') {
      // Store the action to be executed after user state is determined
      setPendingModalAction('buy-tabs')
      
      // Clean up URL without refreshing the page
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [])

  // Execute pending modal action after user state is loaded
  useEffect(() => {
    if (pendingModalAction === 'register') {
      // Check if user is already logged in
      const currentUser = API.getCurrentUser()
      
      if (currentUser) {
        // User is logged in, open account modal instead
        setIsAccountOpen(true)
      } else {
        // User is not logged in, open register modal
        setAuthModalMode('register')
        setIsAuthOpen(true)
      }
      
      // Clear pending action
      setPendingModalAction(null)
    } else if (pendingModalAction === 'buy-tabs') {
      // Check if user is already logged in
      const currentUser = API.getCurrentUser()
      
      if (currentUser) {
        // User is logged in, open account modal with buy tab
        setAccountInitialTab('buy')
        setIsAccountOpen(true)
      } else {
        // User is not logged in, open login modal
        setIsAuthOpen(true)
      }
      
      // Clear pending action
      setPendingModalAction(null)
    }
  }, [pendingModalAction, user])

  // Reset scroll to top when transitioning to desktop viewport
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1200px)')
    const handler = (e) => {
      if (e.matches) {
        document.querySelector('.content-wrapper')?.scrollTo(0, 0)
      }
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const handleLogin = (userData) => {
    setUser(userData)
  }

  const handleLogout = async () => {
    await API.Auth.logout()
    setUser(null)
  }

  const handleUserUpdate = (updatedUserData) => {
    // Update user state with new data (e.g., nickname)
    setUser(prevUser => ({
      ...prevUser,
      ...updatedUserData
    }))
  }

  const handleServerStatusChange = () => {
    // Refresh the server status widget when admin changes status
    if (serverStatusRef.current) {
      serverStatusRef.current.refresh()
    }
  }

  const handleAdminClick = () => {
    if (user?.isAdmin) {
      setIsAdminOpen(true)
    }
  }

  return (
    <div className="app">
      <div className="background-image" />
      <div className="parchment-overlay" />
      
      <div className="content-wrapper">
        <Header 
          user={user}
          onAdminClick={handleAdminClick}
        />
        <main>
          <Hero 
            user={user}
            onAuthClick={() => setIsAuthOpen(true)}
            onAccountClick={() => setIsAccountOpen(true)}
          />
          <Features />
          <Announcements />
          <Download />
        </main>
        <Footer onAboutClick={() => setIsAboutOpen(true)} />
      </div>
      
      {/* Dark overlay in front of everything */}
      <NightOverlay />
      
      {/* Server Status Widget */}
      <ServerStatus ref={serverStatusRef} />
      
      {/* About Modal */}
      <AboutModal isOpen={isAboutOpen} onClose={() => setIsAboutOpen(false)} />
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={isAuthOpen} 
        onClose={() => {
          setIsAuthOpen(false)
          // Reset mode and token when modal closes
          setTimeout(() => {
            setAuthModalMode('login')
            setResetToken(null)
            setUnverifiedEmail(null)
          }, 300)
        }} 
        onLogin={handleLogin}
        initialMode={authModalMode}
        resetToken={resetToken}
        unverifiedEmail={unverifiedEmail}
        onSetUnverifiedEmail={setUnverifiedEmail}
      />

      {/* Account Modal */}
      <AccountModal
        isOpen={isAccountOpen}
        onClose={() => {
          setIsAccountOpen(false)
          // Reset initial tab when modal closes
          setTimeout(() => setAccountInitialTab('info'), 300)
        }}
        user={user}
        onLogout={handleLogout}
        onUserUpdate={handleUserUpdate}
        initialTab={accountInitialTab}
      />

      {/* Admin Panel */}
      <AdminPanel
        isOpen={isAdminOpen}
        onClose={() => setIsAdminOpen(false)}
        user={user}
        onServerStatusChange={handleServerStatusChange}
      />

      {/* Session Invalidation Notification */}
      {sessionMessage && (
        <div className="session-notification">
          <div className="session-notification__content">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/>
            </svg>
            <span>{sessionMessage}</span>
            <button onClick={() => setSessionMessage(null)}>×</button>
          </div>
        </div>
      )}

      {/* Download notification */}
      <DownloadNotification />
    </div>
  )
}

export default App
